export class NetworkMonitor {
  private isOnline: boolean = navigator.onLine;
  private onlineCallbacks: (() => void)[] = [];
  private offlineCallbacks: (() => void)[] = [];
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.initializeEventListeners();
    this.verifyConnectivity();
  }

  private initializeEventListeners(): void {
    window.addEventListener("online", this.handleOnline.bind(this));
    window.addEventListener("offline", this.handleOffline.bind(this));
  }

  private handleOnline(): void {
    this.isOnline = true;
    console.log("Network Monitor: Connection restored");
    this.onlineCallbacks.forEach((callback) => callback());
  }

  private handleOffline(): void {
    this.isOnline = false;
    console.log("Network Monitor: Connection lost");
    this.offlineCallbacks.forEach((callback) => callback());
  }

  private async verifyConnectivity(): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      await fetch(`${this.baseURL}/ping/`, {
        method: "GET",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      this.isOnline = true;
    } catch {
      this.isOnline = false;
    }
  }

  public isConnected(): boolean {
    return this.isOnline;
  }

  public onOnline(callback: () => void): void {
    this.onlineCallbacks.push(callback);
  }

  public onOffline(callback: () => void): void {
    this.offlineCallbacks.push(callback);
  }

  public removeOnlineCallback(callback: () => void): void {
    this.onlineCallbacks = this.onlineCallbacks.filter((cb) => cb !== callback);
  }

  public removeOfflineCallback(callback: () => void): void {
    this.offlineCallbacks = this.offlineCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  public async recheckConnectivity(): Promise<boolean> {
    await this.verifyConnectivity();
    return this.isOnline;
  }

  public destroy(): void {
    window.removeEventListener("online", this.handleOnline.bind(this));
    window.removeEventListener("offline", this.handleOffline.bind(this));
    this.onlineCallbacks = [];
    this.offlineCallbacks = [];
  }
}
